import { useEffect, useRef } from 'react';

/**
 * Custom hook for polling - calls a callback function at specified intervals
 * 
 * Features:
 * - Calls callback function at specified interval
 * - Only polls when enabled flag is true
 * - Cleans up interval on unmount or when disabled
 * - Handles async callbacks safely
 * 
 * @param callback - Async function to call at each interval
 * @param interval - Interval in milliseconds between calls
 * @param enabled - Whether polling is enabled
 */
export function usePolling(
  callback: () => Promise<void>,
  interval: number,
  enabled: boolean
): void {
  const callbackRef = useRef(callback);
  const intervalRef = useRef<number | null>(null);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // Only set up polling if enabled
    if (!enabled) {
      // Clear any existing interval if disabled
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Clear any existing interval before setting up new one
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }

    // Set up interval to call callback
    intervalRef.current = window.setInterval(() => {
      // Call the callback and handle any errors
      callbackRef.current().catch((error) => {
        console.error('Polling callback error:', error);
      });
    }, interval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [interval, enabled]);
}
